<template>
  <modal
    name="view-customer-accounts-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '50%'"
    :scrollable="true"
    @opened="onOpen"
  >
    <!-- Header -->
    <div
      class="pt-3 pb-2 pr-4 pl-4 h-12 border-gray-100 dark:border-gray-500 dark:bg-gray-900 dark:text-gray-200"
    >
      <h4 class="inline-block w-11/12">
        Müşteri Hesapları - {{ customerName }}
      </h4>
      <button @click="$modal.hide('view-customer-accounts-modal')" class="w-1/12">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <!-- Header -->

    <!-- Body -->
    <div
      class="p-4 bg-white dark:bg-gray-900 text-black dark:text-gray-200 border-gray-200 border-b border-t border-opacity-30 overflow-y-auto h-full pb-20 xl:h-auto xl:pb-3"
    >
      <!-- Loading -->
      <div v-if="load" class="flex items-center justify-center py-8">
        <i class="fas fa-spinner fa-spin text-gray-400 text-xl"></i>
        <span class="ml-2 text-sm text-gray-500">Yükleniyor...</span>
      </div>

      <!-- No accounts -->
      <div v-else-if="accounts.length === 0" class="text-center py-8">
        <i class="fas fa-user-slash text-gray-300 text-3xl mb-3"></i>
        <p class="text-sm text-gray-500">Bu müşteriye ait hesap bulunamadı.</p>
      </div>

      <!-- Account list -->
      <div v-else class="space-y-3">
        <div
          v-for="account in accounts"
          :key="account.id"
          class="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600"
        >
          <!-- View mode -->
          <template v-if="editingId !== account.id">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <i class="fas fa-user-circle text-green-600"></i>
                <span class="font-medium text-sm">{{ account.namesurname }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="text-xxs px-2 py-0.5 rounded"
                  :class="account.activeState === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                >
                  {{ account.activeState === 1 ? 'Aktif' : 'Pasif' }}
                </span>
                <button
                  @click="startEdit(account)"
                  class="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                  title="Düzenle"
                >
                  <i class="fas fa-pen text-gray-500 text-xs"></i>
                </button>
                <button
                  @click="deleteAccount(account)"
                  class="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded transition-colors"
                  title="Sil"
                >
                  <i class="fas fa-trash text-red-500 text-xs"></i>
                </button>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <span class="text-gray-400">Kullanıcı Adı:</span>
                <span class="ml-1 font-medium">{{ account.username }}</span>
              </div>
              <div>
                <span class="text-gray-400">E-posta:</span>
                <span class="ml-1">{{ account.email || '-' }}</span>
              </div>
              <div>
                <span class="text-gray-400">Pozisyon:</span>
                <span class="ml-1">{{ account.authority || '-' }}</span>
              </div>
              <div>
                <span class="text-gray-400">Eklenme:</span>
                <span class="ml-1">{{ formatDate(account.addDate) }}</span>
              </div>
            </div>
          </template>

          <!-- Edit mode -->
          <template v-else>
            <form @submit.prevent="saveEdit" class="space-y-3">
              <div class="flex items-center justify-between mb-1">
                <span class="text-sm font-medium text-blue-600">
                  <i class="fas fa-pen mr-1"></i> Hesap Düzenleme
                </span>
                <button type="button" @click="cancelEdit" class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="text-xxs text-gray-500 block mb-1">Ad Soyad</label>
                  <input
                    v-model="editForm.namesurname"
                    type="text"
                    required
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label class="text-xxs text-gray-500 block mb-1">Kullanıcı Adı</label>
                  <input
                    v-model="editForm.username"
                    type="text"
                    required
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label class="text-xxs text-gray-500 block mb-1">E-posta</label>
                  <input
                    v-model="editForm.email"
                    type="email"
                    required
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label class="text-xxs text-gray-500 block mb-1">Pozisyon</label>
                  <input
                    v-model="editForm.authority"
                    type="text"
                    required
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label class="text-xxs text-gray-500 block mb-1">Yeni Parola (opsiyonel)</label>
                  <input
                    v-model="editForm.password"
                    type="password"
                    placeholder="Değiştirmek istemiyorsanız boş bırakın"
                    class="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  />
                </div>
              </div>
              <div class="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  @click="cancelEdit"
                  class="px-3 py-1.5 text-xs text-gray-600 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  :disabled="editLoad"
                  class="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                >
                  <i v-if="editLoad" class="fas fa-spinner fa-spin mr-1"></i>
                  Kaydet
                </button>
              </div>
            </form>
          </template>
        </div>
      </div>
    </div>
    <!-- Body -->
  </modal>
</template>

<script>
import axios from "axios";
import { managementUser } from "@/networking/urlmanager";
import moment from "moment";

export default {
  name: "view-customer-accounts-modal",
  props: ["customer"],
  data() {
    return {
      load: false,
      editLoad: false,
      accounts: [],
      editingId: null,
      editForm: {
        namesurname: "",
        username: "",
        email: "",
        authority: "",
        password: "",
      },
    };
  },
  computed: {
    customerName() {
      return this.customer?.name || "";
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD.MM.YYYY");
    },
    onOpen() {
      this.accounts = [];
      this.editingId = null;
      if (!this.customer?.id) return;
      this.fetchAccounts();
    },
    fetchAccounts() {
      this.load = true;
      axios
        .get(managementUser.getAll + "?page=0&pageSize=100", {
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          },
        })
        .then((res) => {
          const allUsers = res.data.data?.detail || [];
          this.accounts = allUsers.filter((user) => {
            if (user.userType !== 2) return false;
            let customers = user.customers;
            if (typeof customers === "string") {
              try {
                customers = JSON.parse(customers);
              } catch {
                return false;
              }
            }
            if (!Array.isArray(customers)) return false;
            return customers.some(
              (c) => c && c.customerId === this.customer.id
            );
          });
        })
        .catch((err) => {
          this.errorBox(err.response);
        })
        .finally(() => {
          this.load = false;
        });
    },
    startEdit(account) {
      this.editingId = account.id;
      this.editForm = {
        namesurname: account.namesurname,
        username: account.username,
        email: account.email || "",
        authority: account.authority || "",
        password: "",
      };
    },
    cancelEdit() {
      this.editingId = null;
    },
    saveEdit() {
      this.editLoad = true;
      const payload = {
        rowId: this.editingId.toString(),
        namesurname: this.editForm.namesurname,
        username: this.editForm.username,
        email: this.editForm.email,
        authority: this.editForm.authority,
      };
      if (this.editForm.password) {
        payload.password = this.editForm.password;
      }
      axios
        .post(managementUser.edit, payload, {
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          },
        })
        .then(() => {
          this.editingId = null;
          this.swalBox(
            "success",
            this.$t("general.successTitle"),
            "Hesap başarıyla güncellendi",
            false,
            this.$t("general.OkayTitle")
          );
          this.fetchAccounts();
        })
        .catch((err) => {
          this.errorBox(err.response);
        })
        .finally(() => {
          this.editLoad = false;
        });
    },
    deleteAccount(account) {
      this.swalBox(
        "warning",
        this.$t("general.warningTitle"),
        `"${account.namesurname}" hesabını silmek istediğinize emin misiniz?`,
        true,
        this.$t("general.confirmText"),
        this.$t("general.rejectText")
      ).then((acc) => {
        if (acc.isConfirmed) {
          axios
            .post(
              managementUser.passive,
              {
                rowId: account.id.toString(),
                fullName: account.namesurname,
                userName: account.username,
              },
              {
                headers: {
                  Authorization: "Bareer " + this.$store.state.userData.token,
                },
              }
            )
            .then(() => {
              this.swalBox(
                "success",
                this.$t("general.successTitle"),
                "Hesap başarıyla silindi",
                false,
                this.$t("general.OkayTitle")
              );
              this.fetchAccounts();
            })
            .catch((err) => {
              this.errorBox(err.response);
            });
        }
      });
    },
  },
};
</script>
